import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import FadeInSection from "../../FadeInup/FadeInup";

const EfficiencyProgress = () => {
    const [currentWatt, setCurrentWatt] = useState(82);
    const progressBarRef = useRef(null);
    
    // Memoize the calculation functions to prevent unnecessary recalculations
    const calculateColor = useCallback(() => {
        const startColor = "#16DFFF"; // Orange color at the start (82 watt)
        const endColor = "#16DFFF"; // Blue color at the end (28 watt)
        const progressPercentage = ((82 - currentWatt) / 54) * 100;
        const red = Math.round(((100 - progressPercentage) / 100) * parseInt(startColor.slice(1, 3), 16) + (progressPercentage / 100) * parseInt(endColor.slice(1, 3), 16));
        const green = Math.round(((100 - progressPercentage) / 100) * parseInt(startColor.slice(3, 5), 16) + (progressPercentage / 100) * parseInt(endColor.slice(3, 5), 16));
        const blue = Math.round(((100 - progressPercentage) / 100) * parseInt(startColor.slice(5, 7), 16) + (progressPercentage / 100) * parseInt(endColor.slice(5, 7), 16));
        return `rgb(${red}, ${green}, ${blue})`;
    }, [currentWatt]);

    const calculateOffset = useCallback(() => {
        const radius = 80; // Adjust the radius based on the desired circle size
        const circumference = Math.PI * 2 * radius * 1.55; // 89% of the circumference
        const progress = ((currentWatt - 28) / 54) * circumference; // Reverse the calculation
        return circumference - progress;
    }, [currentWatt]);

    // Memoize constant values
    const radius = useMemo(() => 160, []);
    const circumference = useMemo(() => Math.PI * 2 * radius * 0.89, [radius]);

    // Use useCallback for the scroll handler to prevent unnecessary re-renders
    const handleScroll = useCallback(() => {
        let timeoutId;
        
        const checkVisibility = () => {
            clearTimeout(timeoutId);
            const rect = progressBarRef?.current?.getBoundingClientRect();
            const isVisible = rect?.top < window.innerHeight && rect?.bottom >= 0;

            if (isVisible) {
                clearTimeout(timeoutId);
                timeoutId = setTimeout(() => {
                    const interval = setInterval(() => {
                        setCurrentWatt((prevWatt) => (prevWatt > 28 ? prevWatt - 1 : prevWatt));
                    }, 50);

                    return () => clearInterval(interval);
                }, 600); // Adjust the buffer time as needed
            }
        };
        
        window.addEventListener('scroll', checkVisibility);
        
        return () => {
            clearTimeout(timeoutId);
            window.removeEventListener('scroll', checkVisibility);
        };
    }, []);

    useEffect(() => {
        const cleanup = handleScroll();
        return cleanup;
    }, [handleScroll]);

    return (
        <div className="container-fluid homeprogress">
            <div className="row pt-5">
                <div className="col-md-6 loaderalign">
                    <div className="progress-loader" ref={progressBarRef}>
                        <svg className="circle">
                            <circle className="circle-background" cx="330" cy="180" r={radius} />
                            <circle
                                className="circle-progress"
                                cx="330"
                                cy="180"
                                r={radius}
                                stroke={calculateColor()}
                                strokeDasharray={`${circumference}`}
                                strokeDashoffset={calculateOffset()}
                            />
                            <circle
                                className="circle-dot"
                                cx="150"
                                cy={radius * 0.25} // Adjust the y-coordinate of the dot based on the desired position
                                r="5"
                                fill={calculateColor()}
                                transform={`rotate(${calculateOffset() / circumference} 150 150)`}
                            />
                            <image className="circle-image" xlinkHref="https://colorbots3.s3.ap-south-1.amazonaws.com/progressvector.svg" x="300" y="150" width="50" height="50" alt="A ring structure shows how Colorbot Ceiling fans consume only 28 watts of electricity while normal takes around 80 watts" />
                            <text className="watt-text" x="350" y="150" fill={calculateColor()}>
                                {currentWatt}
                            </text>
                            <text className="subwatt-text" x="370" y="150" fill={calculateColor()}>
                                Watts*
                            </text>
                        </svg>
                    </div>
                </div>
                <div className="col-md-6 homealigndark">
                    <FadeInSection>
                        <h2 className="hometitledark">Efficiency Redefined</h2>
                        <h3 className="subtitledark">Endless Breeze. Limitless Power</h3>
                        <br />
                        <p className="homesubtitledark">
                            <p className='homeleft'>
                                Colorbot BLDC fans consume only up to 28 watts of electricity at the maximum speed, and even lesser at
                                lower speeds, saving around 67% of energy. The ultimate efficiency of Colorbot fans lets you experience
                                a cooler summer even when the fans are used round the clock.
                            </p>
                            <br />
                            <br />
                            <p style={{ fontSize: "12px", color: "gray" }}><i>*All Colorbot BLDC fans consume ~28 watts at speed 5.</i></p>
                        </p>
                    </FadeInSection>
                </div>
            </div>
        </div>
    );
};

export default React.memo(EfficiencyProgress);
